import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../services/api';
import './Login.css';

const Login: React.FC = () => {
  const [empNo, setEmpNo] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [loginStep, setLoginStep] = useState<'credentials' | 'otp'>('credentials');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [requireCodeNext] = useState(false);

  const { login, completeLogin } = useAuth();
  const navigate = useNavigate();

  const getErrorMessage = (err: any, fallback: string) => {
    if (err?.response?.data?.message) {
      const message = err.response.data.message;
      if (Array.isArray(message)) return message.join(', ');
      if (typeof message === 'string') return message;
    }
    if (typeof err?.message === 'string' && err.message.trim()) {
      if (err.message === 'Network Error') {
        return 'Cannot reach API server. Check backend is running and CORS/API URL settings are correct.';
      }
      return err.message;
    }
    return fallback;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await login(empNo, password);
      if (!response?.verificationId) {
        throw new Error('Login response is invalid. Missing verification details.');
      }
      setVerificationId(response.verificationId);
      setMaskedEmail(response.email);
      setLoginStep('otp');
    } catch (err: any) {
      setError(getErrorMessage(err, 'Login failed. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!verificationId) {
        throw new Error('Missing verification session. Please sign in again.');
      }
      await completeLogin(verificationId, verificationCode);
      navigate('/');
    } catch (err: any) {
      setError(getErrorMessage(err, 'Verification failed. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setTwoFactorError('');
    setTwoFactorLoading(true);

    try {
      await verify2FA(tempToken, twoFactorCode);
      navigate('/');
    } catch (err: any) {
      setTwoFactorError(err.response?.data?.message || 'Invalid or expired code. Please sign in again.');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      });
      const data = await response.json();
      setResetMessage(data.message || 'Reset instructions sent');
    } catch (err) {
      setResetMessage('Failed to send reset email');
    }
  };

  const renderForgotPassword = () => (
    <div className="login-page">
      <div className="login-shell">
        <div className="login-card">
          <div className="login-hero">
            <img src="/toyota-lanka-logo.png" alt="Toyota Lanka" className="logo" />
            <h1>Toyota Lanka</h1>
            <p className="subtitle">Employee Intranet Portal</p>
          </div>

          <div className="login-header">
            <div className="pill">Reset password</div>
            <h2>Let’s get you back in</h2>
            <p className="form-description">Enter your email to receive reset instructions.</p>
          </div>

          <div className="login-form-container">
            {resetMessage && <div className="message-box success">{resetMessage}</div>}

            <form onSubmit={handleForgotPassword} className="form-stack">
              <div className="form-group">
                <label htmlFor="resetEmail">Email address</label>
                <input
                  type="email"
                  id="resetEmail"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="name@company.com"
                  required
                />
              </div>

              <button type="submit" className="btn-primary">
                Send reset link
              </button>

              <button
                type="button"
                className="btn-link"
                onClick={() => setShowForgotPassword(false)}
              >
                Back to sign in
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );

  const render2FA = () => (
    <div className="login-page">
      <div className="login-shell">
        <div className="login-card">
          <div className="login-hero">
            <img src="/toyota-lanka-logo.png" alt="Toyota Lanka" className="logo" />
            <h1>Toyota Lanka</h1>
            <p className="subtitle">Employee Intranet Portal</p>
          </div>

          <div className="login-header">
            <div className="pill">Two-factor verification</div>
            <h2>Enter your verification code</h2>
            <p className="form-description">
              We sent a 6-digit code to your company email. Enter it below.
            </p>
          </div>

          <div className="login-form-container">
            {twoFactorError && <div className="error-message">{twoFactorError}</div>}

            <form onSubmit={handleVerify2FA} className="form-stack">
              <div className="form-group">
                <label htmlFor="twoFactorCode">Verification code</label>
                <input
                  type="text"
                  id="twoFactorCode"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  required
                />
              </div>

              <button type="submit" className="btn-primary" disabled={twoFactorLoading}>
                {twoFactorLoading ? 'Verifying...' : 'Verify and sign in'}
              </button>

              <button
                type="button"
                className="btn-link"
                onClick={() => {
                  setShow2FA(false);
                  setTempToken('');
                  setTwoFactorCode('');
                  setTwoFactorError('');
                }}
              >
                Back to sign in
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );

  if (show2FA) return render2FA();
  if (showForgotPassword) return renderForgotPassword();

  return (
    <div className="login-page">
      <div className="login-shell">
        <div className="login-card">
          <div className="login-hero">
            <img src="/toyota-lanka-logo.png" alt="Toyota Lanka" className="logo" />
            <h1>Toyota Lanka</h1>
            <p className="subtitle">Employee Intranet Portal</p>
          </div>

          <div className="login-header">
            <div className="pill">Welcome back</div>
            <h2>{loginStep === 'credentials' ? 'Sign in to continue' : 'Two-factor verification'}</h2>
            <p className="form-description">
              {loginStep === 'credentials'
                ? 'Access your dashboard, documents, and updates.'
                : `Enter the 6-digit code sent to ${maskedEmail}.`}
            </p>
          </div>

          <div className="login-form-container">
            {error && <div className="error-message">{error}</div>}
            {requireCodeNext && (
              <div className="message-box success" style={{ marginBottom: '0.75rem' }}>
                Next sign-in will require your email verification code.
              </div>
            )}

            {loginStep === 'credentials' ? (
              <form onSubmit={handleSubmit} className="form-stack">
                <div className="form-group">
                  <label htmlFor="empNo">Employee number</label>
                  <input
                    type="number"
                    id="empNo"
                    value={empNo}
                    onChange={(e) => setEmpNo(e.target.value)}
                    placeholder="Enter your employee number"
                    required
                  />
                </div>

                <div className="form-group password-group">
                  <label htmlFor="password">Password</label>
                  <div className="password-field">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      className="toggle-visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>

                <button type="submit" className="btn-primary" disabled={isLoading}>
                  {isLoading ? 'Sending code...' : 'Sign in'}
                </button>

                <button
                  type="button"
                  className="btn-link"
                  onClick={() => setShowForgotPassword(true)}
                >
                  Forgot password?
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifySubmit} className="form-stack">
                <div className="form-group">
                  <label htmlFor="verificationCode">Verification code</label>
                  <input
                    type="text"
                    id="verificationCode"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    required
                  />
                </div>

                <button type="submit" className="btn-primary" disabled={isLoading}>
                  {isLoading ? 'Verifying...' : 'Verify and sign in'}
                </button>

                <button
                  type="button"
                  className="btn-link"
                  onClick={() => {
                    setLoginStep('credentials');
                    setVerificationCode('');
                    setVerificationId('');
                    setMaskedEmail('');
                    setError('');
                    setEmpNo('');
                    setPassword('');
                  }}
                >
                  Back
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
async function verify2FA(tempToken: string, twoFactorCode: string) {
  const response = await fetch(`${API_BASE_URL}/auth/2fa/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      tempToken,
      code: twoFactorCode,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Two-factor verification failed');
  }
  
  return response.json();
}

